import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MicIcon, MicOffIcon, SquareIcon } from "lucide-react";
import type { AnswerState, PronounceExercise } from "../../types/exercises";
import { SpeakButton } from "./SpeakButton";
import { canRecognize, createRecognizer, Recognizer } from "../../utils/speech";
import { normalize } from "../../utils/text";
import { spring } from "../../utils/motion";
interface PronounceProps {
  exercise: PronounceExercise;
  onAnswer: (a: AnswerState) => void;
  checked: boolean;
  speechLang: string;
}
type MicState = "idle" | "listening" | "done" | "error";
export function Pronounce({
  exercise,
  onAnswer,
  checked,
  speechLang
}: PronounceProps) {
  const reduce = useReducedMotion() ?? false;
  const supported = canRecognize();
  const [mic, setMic] = useState<MicState>("idle");
  const [error, setError] = useState("");
  const [heard, setHeard] = useState<Set<string> | null>(null);
  const [levels, setLevels] = useState([0.3, 0.5, 0.4, 0.6, 0.35]);
  const recRef = useRef<Recognizer | null>(null);
  const words = exercise.phrase.split(" ");
  useEffect(() => {
    if (mic !== "listening" || reduce) return;
    const id = window.setInterval(() => setLevels(l => l.map(() => 0.25 + Math.random() * 0.75)), 140);
    return () => window.clearInterval(id);
  }, [mic, reduce]);
  useEffect(() => () => recRef.current?.abort(), []);
  const start = () => {
    const rec = createRecognizer(speechLang);
    if (!rec) return;
    recRef.current = rec;
    setError("");
    setHeard(null);
    rec.onresult = e => {
      const transcripts = Array.from(e.results[0] ?? []).map(a => a.transcript);
      const said = new Set(normalize(transcripts.join(" ")).split(" "));
      setHeard(said);
      const hits = words.filter(w => said.has(normalize(w))).length;
      const score = hits / words.length;
      onAnswer({
        ready: true,
        correct: score >= 0.6,
        solution: exercise.phrase
      });
      setMic("done");
    };
    rec.onerror = e => {
      setMic("error");
      setError(e.error === "not-allowed" ? "Microphone access is blocked. Allow it in your browser settings, or skip this one." : "We couldn't hear you. Try again a little closer to the mic.");
    };
    rec.onend = () => setMic(m => m === "listening" ? "idle" : m);
    rec.start();
    setMic("listening");
  };
  const stop = () => recRef.current?.stop();
  return <div className="flex flex-col items-center text-center" data-id="element-405" data-mp="lplz8352qsnu">
      <div className="flex items-center gap-3" data-id="element-406" data-mp="lo15gfql3dr1">
        <SpeakButton text={exercise.phrase} lang={speechLang} data-id="element-407" data-mp="qg2dfpgpyib9" />
        <SpeakButton text={exercise.phrase} lang={speechLang} slow data-id="element-408" data-mp="qiwvxbg69t21" />
      </div>
      <p className="mt-5 flex flex-wrap justify-center gap-x-2 text-3xl font-bold tracking-[-0.01em] md:text-4xl" lang="es" data-id="element-409" data-mp="vevpddgcy19b">
        {words.map((w, i) => {
        const hit = heard?.has(normalize(w));
        return <span key={i} className={heard ? hit ? "text-good underline decoration-4 underline-offset-8" : "text-bad underline decoration-wavy decoration-2 underline-offset-8" : "text-ink"} data-id="element-410" data-mp="2pvvr711kpfsb" data-mpi={i}>
              {w}
            </span>;
      })}
      </p>
      <p className="mt-3 text-[15px] text-ink-2" data-id="element-411" data-mp="2azejqkwi8lb1">{exercise.translation}</p>

      {supported ? <div className="mt-10 flex flex-col items-center" data-id="element-412" data-mp="38mmxtuu6es30">
          <motion.button type="button" onClick={mic === "listening" ? stop : start} disabled={checked} aria-label={mic === "listening" ? "Stop recording" : "Start speaking"} whileHover={checked || reduce ? undefined : {
        y: -2
      }} whileTap={{
        scale: 0.94
      }} transition={spring.snappy} className={`focus-ring grid h-24 w-24 place-items-center rounded-full border-3 border-line shadow-brutal transition-colors duration-150 ${mic === "listening" ? "bg-bad text-night" : "bg-brand text-white"}`} data-id="element-413" data-mp="38o7rli8o26zt">
            {mic === "listening" ? <SquareIcon className="h-8 w-8 fill-current" data-id="element-414" data-mp="3c49e6q97tect" /> : <MicIcon className="h-10 w-10" strokeWidth={2.25} data-id="element-415" data-mp="3c0vaneotez3s" />}
          </motion.button>
          <div className="mt-5 flex h-10 items-end gap-1.5" aria-hidden="true" data-id="element-416" data-mp="2x81pbcmv3or7">
            {levels.map((l, i) => <motion.span key={i} animate={{
          scaleY: mic === "listening" ? l : 0.15
        }} transition={spring.snappy} className="block h-10 w-2.5 origin-bottom rounded-full border-2 border-line bg-accent" data-id="element-417" data-mp="3pumm4sm3wt25" data-mpi={i} />)}
          </div>
          <p className="mt-3 text-sm font-semibold text-ink-2" aria-live="polite" data-id="element-418" data-mp="3l3e8fkdqulj6">
            {mic === "listening" ? "Listening… tap to stop" : mic === "done" ? "Tap the mic to try again" : "Tap the mic and say the phrase"}
          </p>
          {error && <p className="mt-2 max-w-sm text-sm font-medium text-bad" data-id="element-419" data-mp="hg45i2i3o56x">{error}</p>}
        </div> : <div className="mt-10 max-w-sm rounded-card border-3 border-dashed border-line/50 p-5" data-id="element-420" data-mp="223dop1tve3i5">
          <MicOffIcon className="mx-auto h-8 w-8 text-ink-2" data-id="element-421" data-mp="1x5vbgz8tl24c" />
          <p className="mt-3 text-[15px] text-ink-2" data-id="element-422" data-mp="2bvb28f8cp7k1">
            Speech recognition isn't available in this browser (try Chrome or Edge). Listen, say it out loud, and mark it yourself.
          </p>
          <button type="button" disabled={checked} onClick={() => onAnswer({
        ready: true,
        correct: true,
        solution: exercise.phrase
      })} className="focus-ring mt-4 rounded-xl border-3 border-line bg-surface px-4 py-2 text-sm font-semibold text-ink shadow-brutal-sm" data-id="element-423" data-mp="1j7f4r8ayjbm7">
            I said it out loud
          </button>
        </div>}
    </div>;
}